import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, BUNIA_CENTER, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { connectSocket, getSocket } from "../../lib/socket";
import { getPreciseLocation, watchPreciseLocation, PRECISE_GPS_OPTIONS } from "../../lib/preciseLocation";
import { playNotificationSound } from "../../lib/notificationSound";
import MapView from "../../components/MapView";
import BottomNav from "../../components/BottomNav";
import SOSButton from "../../components/SOSButton";
import RatingModal from "../../components/RatingModal";
import AiInsightCard from "../../components/AiInsightCard";
import InstallAppPrompt from "../../components/InstallAppPrompt";
import { Button, Badge, Input, StatusBadge } from "../../components/ui-kit";
import { startLiveKitAudioCall, stopLiveKitAudioCall } from "../../lib/livekitCall";
import { CheckCircle, NavigationArrow, Lightning, Package, Phone } from "@phosphor-icons/react";

export default function DriverHome() {
  const { user, profile, refresh } = useAuth();
  const workerRole = user?.role === "courier" ? "courier" : "driver";
  const workerBase = workerRole === "courier" ? "/courier" : "/driver";
  const [online, setOnline] = useState(false);
  const [location, setLocation] = useState(BUNIA_CENTER);
  const [requests, setRequests] = useState([]);
  const [requestMeta, setRequestMeta] = useState({});
  const [activeRide, setActiveRide] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [rateOpen, setRateOpen] = useState(false);
  const [lastCompletedId, setLastCompletedId] = useState(null);
  const [liveCall, setLiveCall] = useState(false);
  const [calling, setCalling] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const watchIdRef = useRef(null);
  const lastSentRef = useRef(0);
  const knownRequestIdsRef = useRef(new Set());
  const autoAcceptedRef = useRef(new Set());

  const isApproved = profile?.driver_profile?.approval_status === "ACTIVE";
  const getFreshLocation = useCallback(() => (
    getPreciseLocation().catch(() => {
      throw new Error("Autorisez le GPS precis pour recevoir les demandes proches");
    })
  ), []);
  const requestReasonText = (() => {
    const reason = requestMeta.reason;
    if (reason === "offline") return "Vous etes hors ligne. Activez le bouton EN LIGNE pour recevoir les demandes.";
    if (reason === "vehicle_not_active") return "Votre vehicule n'est pas actif. Verifiez le profil et les documents chauffeur.";
    if (reason === "vehicle_type_missing") return "Le type de vehicule manque dans votre profil chauffeur.";
    if (reason === "service_mode_disabled") return "Les demandes taxi sont desactivees dans vos preferences chauffeur.";
    if (reason === "no_compatible_nearby_requests") {
      const types = (requestMeta.compatible_vehicle_types || []).join(", ");
      return `Il y a ${requestMeta.open_rides_count || 0} demande(s), mais aucune compatible avec votre vehicule${types ? ` (${types})` : ""} ou assez proche.`;
    }
    return "Aucune demande pour le moment";
  })();

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/requests");
      setRequests(data.requests || []);
      setRequestMeta(data || {});
    }
    catch { /* noop */ }
  }, []);
  const fetchActive = useCallback(async () => {
    try { const { data } = await api.get("/driver/active-ride"); setActiveRide(data.ride || null); }
    catch { /* noop */ }
  }, []);
  const acceptRide = useCallback(async (rideId) => {
    try { await api.post(`/rides/${rideId}/accept`); toast.success("Course acceptee"); fetchActive(); fetchRequests(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [fetchActive, fetchRequests]);

  useEffect(() => {
    fetchRequests(); fetchActive();
    const int = setInterval(() => { fetchRequests(); fetchActive(); }, 3000);
    let cleanup = null;
    (async () => {
      const socket = await connectSocket();
      if (!socket) return;
      socket.connect();
      const onRide = (r) => {
        if (r?.driver_id === user?.id) setActiveRide(r);
        if (r?.status === "SEARCHING_DRIVER") fetchRequests();
      };
      const onAvailable = () => fetchRequests();
      socket.on("ride.updated", onRide);
      socket.on("ride.available", onAvailable);
      cleanup = () => {
        socket.off("ride.updated", onRide);
        socket.off("ride.available", onAvailable);
      };
    })();
    return () => { clearInterval(int); if (cleanup) cleanup(); };
  }, [fetchRequests, fetchActive, user?.id]);

  useEffect(() => {
    if (!online || requests.length === 0) {
      knownRequestIdsRef.current = new Set(requests.map((r) => r.id));
      return;
    }
    const known = knownRequestIdsRef.current;
    const hasNew = requests.some((r) => !known.has(r.id));
    knownRequestIdsRef.current = new Set(requests.map((r) => r.id));
    if (hasNew) {
      playNotificationSound("urgent");
      toast("Nouvelle demande de course", {
        description: "Une demande compatible est disponible.",
        duration: 7000,
      });
    }
  }, [requests, online]);

  useEffect(() => {
    const prefs = requestMeta.preferences || {};
    if (!online || activeRide || !prefs.auto_accept || requests.length === 0) return;
    const first = requests[0];
    if (!first?.id || autoAcceptedRef.current.has(first.id)) return;
    autoAcceptedRef.current.add(first.id);
    acceptRide(first.id);
  }, [requests, requestMeta.preferences, online, activeRide, acceptRide]);

  useEffect(() => {
    setOnline(profile?.driver_profile?.status === "online");
    if (profile?.driver_profile?.current_location) {
      setLocation(profile.driver_profile.current_location);
    }
  }, [profile]);

  // Real GPS tracking — only when online + approved
  useEffect(() => {
    if (profile?.driver_profile?.current_location || !("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_m: pos.coords.accuracy });
      },
      () => {},
      PRECISE_GPS_OPTIONS
    );
  }, [profile?.driver_profile?.current_location]);

  useEffect(() => {
    const stop = () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
    if (!online || !isApproved) { stop(); return; }
    if (!("geolocation" in navigator)) {
      toast.error("GPS non disponible sur cet appareil");
      return;
    }
    watchIdRef.current = watchPreciseLocation(
      async (loc) => {
        setLocation(loc);
        const now = Date.now();
        // Throttle to 1 update every 4s
        if (now - lastSentRef.current < 4000) return;
        lastSentRef.current = now;
        try { await api.post("/driver/location", loc); } catch { /* noop */ }
        const s = getSocket();
        if (s && s.connected) s.emit("driver_location", loc);
      },
      (err) => {
        console.warn("GPS error:", err);
        if (err.code === 1) toast.error("Autorisation GPS refusée");
      }
    );
    return stop;
  }, [online, isApproved]);

  const toggleOnline = async () => {
    const newStatus = online ? "offline" : "online";
    try {
      const nextLocation = newStatus === "online" ? await getFreshLocation() : location;
      if (newStatus === "online") setLocation(nextLocation);
      await api.post("/driver/status", { status: newStatus, location: nextLocation });
      setOnline(!online);
      toast.success(newStatus === "online" ? "● En ligne" : "Hors ligne");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const arrived = async () => {
    try { await api.post(`/rides/${activeRide.id}/arrived`); fetchActive(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const startRide = async () => {
    try { await api.post(`/rides/${activeRide.id}/start`, { pin: pinInput }); toast.success("Course démarrée"); setPinInput(""); fetchActive(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const completeRide = async () => {
    try {
      const rideId = activeRide.id;
      await api.post(`/rides/${rideId}/complete`);
      setLastCompletedId(rideId);
      setRateOpen(true);
      toast.success("Terminée — notez le client");
      fetchActive();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmPaymentReceived = async () => {
    if (!activeRide) return;
    setConfirmingPayment(true);
    try {
      await api.post(`/payments/${activeRide.id}/confirm-received`, {
        received: true,
        transaction_reference: activeRide.payment_reference || null,
      });
      toast.success("Paiement confirme");
      fetchActive();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmingPayment(false);
    }
  };
  const callCustomer = async () => {
    if (!activeRide) return;
    setCalling(true);
    try {
      const { data } = await api.get(`/rides/${activeRide.id}/livekit-token`);
      await startLiveKitAudioCall({ url: data.url, token: data.token });
      setLiveCall(true);
      toast.success("Appel dans l'app demarre");
    } catch (e) {
      toast.error(formatApiError(e));
      window.location.href = `tel:${activeRide.customer_phone}`;
    } finally {
      setCalling(false);
    }
  };
  const hangup = async () => {
    await stopLiveKitAudioCall();
    setLiveCall(false);
    toast.success("Appel termine");
  };
  const simulateMove = async () => {
    if (!activeRide) return;
    const target = activeRide.status === "IN_PROGRESS" ? activeRide.destination : activeRide.pickup;
    const newLoc = {
      ...location,
      lat: location.lat + (target.lat - location.lat) * 0.5,
      lng: location.lng + (target.lng - location.lng) * 0.5,
      accuracy_m: location.accuracy_m || 8,
    };
    setLocation(newLoc);
    await api.post("/driver/location", newLoc);
    const s = getSocket();
    if (s && s.connected) s.emit("driver_location", newLoc);
  };

  return (
    <div className="phone-frame phone-screen-lock relative pb-28">
      <MapView
        center={activeRide?.pickup || location}
        pickup={activeRide?.pickup}
        destination={activeRide?.destination}
        driver={location}
        driverVehicleType={profile?.vehicle?.vehicle_type || activeRide?.vehicle_type}
        height="100dvh"
        className="phone-map-height"
        showAttribution={false}
      />

      {!isApproved && (
        <div className="map-driver-banner-safe absolute inset-x-0 z-[800] bg-warning/20 backdrop-blur-xl p-2 text-center text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--warning))]" data-testid="pending-banner">
          Compte en attente de validation admin
        </div>
      )}

      <div className="map-top-controls-safe absolute inset-x-0 z-[800] flex items-center justify-between gap-2">
        <button
          onClick={toggleOnline}
          disabled={!isApproved}
          className={`map-top-pill rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-widest press-down min-[380px]:px-4 min-[380px]:py-3 min-[380px]:text-xs ${
            online ? "bg-secondary text-secondary-foreground glow-green" : "glass-strong text-foreground"
          } ${!isApproved ? "opacity-40" : ""}`}
          data-testid="toggle-online"
        >
          {online ? "● EN LIGNE" : "HORS LIGNE"}
        </button>
        <div className="flex min-w-0 shrink-0 items-center gap-2">
          <div className="rounded-full glass-strong px-2.5 py-2 text-[9px] font-bold min-[380px]:px-3 min-[380px]:text-[10px]">
            ★ {profile?.driver_profile?.rating?.toFixed(1) || "5.0"} · {profile?.driver_profile?.total_rides || 0}
          </div>
          {activeRide && <SOSButton rideId={activeRide.id} />}
        </div>
      </div>

      <div className="phone-bottom-panel phone-bottom-panel-compact absolute inset-x-0 bottom-20 z-[800] mx-auto px-2">
        {!activeRide && (
          <div className="mb-3">
            <InstallAppPrompt compact />
          </div>
        )}
        {activeRide ? (
          <div className="glass-strong rounded-3xl p-3 min-[390px]:p-4" data-testid="active-ride-panel">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  {activeRide.service_type === "delivery" ? "Livraison active" : "Course active"}
                </div>
                <h2 className="font-heading text-xl font-bold">{activeRide.customer_name}</h2>
                <div className="font-mono text-[11px] text-muted-foreground">{activeRide.customer_phone}</div>
              </div>
	              <StatusBadge status={activeRide.status} />
	            </div>
	            {activeRide.is_for_someone_else && (
	              <div className="mt-3 rounded-2xl border border-primary/25 bg-primary/10 p-3" data-testid="active-recipient">
	                <div className="text-[10px] font-bold uppercase tracking-widest text-primary">A recuperer</div>
	                <div className="mt-1 font-heading text-lg font-bold">{activeRide.recipient_name}</div>
	                <div className="font-mono text-[11px] text-muted-foreground">{activeRide.recipient_phone}</div>
	                {activeRide.recipient_note && (
	                  <div className="mt-2 rounded-xl bg-black/20 p-2 text-xs text-muted-foreground">{activeRide.recipient_note}</div>
	                )}
	              </div>
	            )}
	            {activeRide.service_type === "delivery" && activeRide.delivery && (
	              <div className="mt-3 rounded-2xl border border-secondary/25 bg-secondary/10 p-3" data-testid="active-delivery">
	                <div className="text-[10px] font-bold uppercase tracking-widest text-secondary">Colis a livrer</div>
	                <div className="mt-1 font-bold">{activeRide.delivery.package_description}</div>
	                <div className="mt-1 text-[11px] text-muted-foreground">
	                  Destinataire: <span className="font-mono text-foreground">{activeRide.recipient_name} - {activeRide.recipient_phone}</span>
	                </div>
	                {activeRide.recipient_note && (
	                  <div className="mt-2 rounded-xl bg-black/20 p-2 text-xs text-muted-foreground">{activeRide.recipient_note}</div>
	                )}
	              </div>
	            )}
	            <AiInsightCard
              endpoint={`/rides/${activeRide.id}/ai`}
              title="IA chauffeur"
              limit={2}
              compact
              className="mt-3"
            />
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-white/5 p-2">
                <div className="text-[9px] uppercase text-muted-foreground">Prix</div>
                <div className="font-mono text-sm font-bold">{formatCDF(activeRide.price)}</div>
              </div>
              <div className="rounded-xl bg-white/5 p-2">
                <div className="text-[9px] uppercase text-muted-foreground">Distance</div>
                <div className="font-mono text-sm font-bold">{activeRide.estimate?.distance_km} km</div>
              </div>
              <div className="rounded-xl bg-white/5 p-2">
                <div className="text-[9px] uppercase text-muted-foreground">Type</div>
                <div className="font-mono text-sm font-bold">{activeRide.vehicle_type}</div>
              </div>
            </div>
            {activeRide.status === "RIDE_COMPLETED" && activeRide.payment_status !== "COMPLETED" && ["cash", "mpesa", "airtel", "orange"].includes(activeRide.payment_method) && (
              <div className="mt-3 rounded-2xl border border-secondary/25 bg-secondary/10 p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-secondary">Paiement direct chauffeur</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Methode: <span className="font-bold text-foreground">{activeRide.payment_method}</span>
                  {activeRide.payment_reference && <> · reference: <span className="font-mono text-foreground">{activeRide.payment_reference}</span></>}
                </div>
                <Button
                  variant="secondary"
                  className="mt-3 w-full"
                  onClick={confirmPaymentReceived}
                  disabled={confirmingPayment}
                  data-testid="btn-confirm-payment"
                >
                  {confirmingPayment ? "Confirmation..." : "J'ai recu l'argent"}
                </Button>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {activeRide.status === "DRIVER_EN_ROUTE" && (
                <Button variant="primary" className="flex-1" onClick={arrived} data-testid="btn-arrived">
                  <NavigationArrow weight="fill" /> {activeRide.service_type === "delivery" ? "Au pickup" : "Je suis arrivé"}
                </Button>
              )}
              {activeRide.status === "DRIVER_ARRIVED" && (
                <div className="flex w-full gap-2">
                  <Input value={pinInput} onChange={(e) => setPinInput(e.target.value)} placeholder={activeRide.service_type === "delivery" ? "PIN expedition" : "PIN client"} className="font-mono text-lg" data-testid="pin-input" />
                  <Button variant="primary" onClick={startRide} data-testid="btn-start">{activeRide.service_type === "delivery" ? "Colis recu" : "Démarrer"}</Button>
                </div>
              )}
              {activeRide.status === "IN_PROGRESS" && (
                <Button variant="secondary" className="flex-1" onClick={completeRide} data-testid="btn-complete">
                  <CheckCircle weight="fill" /> {activeRide.service_type === "delivery" ? "Livree" : "Terminer"}
                </Button>
              )}
              {liveCall ? (
                <Button variant="danger" className="flex-1 text-[10px]" onClick={hangup} data-testid="btn-hangup">
                  Raccrocher
                </Button>
              ) : (
                <Button variant="outline" className="flex-1 text-[10px]" onClick={callCustomer} disabled={calling} data-testid="btn-call-customer">
                  <Phone weight="fill" /> Appeler
                </Button>
              )}
              <Button variant="outline" className="text-[10px]" onClick={simulateMove} data-testid="btn-simulate">
                <Lightning weight="fill" /> Avancer
              </Button>
            </div>
          </div>
        ) : !isApproved ? (
          <div className="glass-strong rounded-3xl p-4" data-testid="driver-validation-required">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--warning))]">
              Validation requise
            </div>
            <h2 className="mt-1 font-heading text-xl font-bold">Activez votre profil chauffeur</h2>
            <div className="mt-2 text-xs text-muted-foreground">
              Completez vos details et envoyez les documents obligatoires pour permettre a l'admin de valider votre compte.
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link to={`${workerBase}/profile`} data-testid="go-driver-profile">
                <Button variant="primary" className="w-full text-xs">Voir le profil</Button>
              </Link>
              <Link to={`${workerBase}/documents`} data-testid="go-driver-documents">
                <Button variant="outline" className="w-full text-xs">Documents</Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="glass-strong rounded-3xl p-3 min-[390px]:p-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold">Demandes entrantes</h2>
              <Badge tone={requests.length > 0 ? "primary" : "default"}>{requests.length}</Badge>
            </div>
            <AiInsightCard title="Assistant IA chauffeur" limit={2} compact className="mt-3" />
            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
              {!online && isApproved && (
                <div className="rounded-xl border border-dashed border-white/10 p-4 text-center text-xs text-muted-foreground">
                  Passez en ligne pour recevoir des {workerRole === "courier" ? "livraisons" : "courses"}
                </div>
              )}
              {online && requests.length === 0 && (
                <div className="rounded-xl border border-dashed border-white/10 p-4 text-center text-xs text-muted-foreground">
                  {requestReasonText}
                  {requestMeta.open_rides_count > 0 && (
                    <div className="mt-2 font-mono text-[10px] text-primary">
                      demandes ouvertes: {requestMeta.open_rides_count} · vehicule: {requestMeta.vehicle_type || "-"}
                    </div>
                  )}
                </div>
              )}
              {requests.map((r) => (
                <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={`req-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
                        <span>#{r.id.slice(0, 6).toUpperCase()} · {r.vehicle_type}</span>
                        {r.service_type === "delivery" && <Badge tone="success"><Package size={11} weight="fill" /> Livraison</Badge>}
                      </div>
                      <div className="font-mono text-lg font-bold">{formatCDF(r.price)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {r.estimate?.distance_km} km · {Math.round(r.estimate?.duration_min)} min
                        {r.distance_to_pickup_km !== undefined && ` · ${r.distance_to_pickup_km} km à pick`}
                      </div>
                      {r.service_type === "delivery" && r.delivery && (
                        <div className="mt-2 rounded-xl border border-secondary/25 bg-secondary/10 p-2 text-[11px]" data-testid={`req-delivery-${r.id}`}>
                          <div className="font-bold text-secondary">{r.delivery.package_description}</div>
                          <div className="mt-1 text-muted-foreground">
                            Destinataire: <span className="font-mono text-foreground">{r.recipient_name} - {r.recipient_phone}</span>
                          </div>
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1">
	                        {r.priority === "high" && <Badge tone="success">Priorite</Badge>}
	                        {r.driver_score && <Badge tone="primary">Score {r.driver_score}</Badge>}
	                        {r.rating_avg && <Badge tone="default">Note {r.rating_avg}</Badge>}
	                        {r.is_for_someone_else && <Badge tone="warning">Pour {r.recipient_name}</Badge>}
	                      </div>
	                      {r.is_for_someone_else && (
	                        <div className="mt-2 rounded-xl bg-primary/10 p-2 text-[11px]" data-testid={`req-recipient-${r.id}`}>
	                          <div className="font-bold text-primary">A recuperer: {r.recipient_name}</div>
	                          <div className="font-mono text-muted-foreground">{r.recipient_phone}</div>
	                          {r.recipient_note && <div className="mt-1 text-muted-foreground">{r.recipient_note}</div>}
	                        </div>
	                      )}
	                    </div>
                    <Button variant="primary" className="px-3 py-2 text-xs" onClick={() => acceptRide(r.id)} data-testid={`accept-${r.id}`}>Accepter</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav role={workerRole} />
      <RatingModal
        open={rateOpen}
        onClose={() => setRateOpen(false)}
        rideId={lastCompletedId}
        targetRole="client"
      />
    </div>
  );
}
